import type { Control, Mapping, Question } from './api/types'

/**
 * Assessment coverage model — derived entirely from data we already fetch
 * (/questions + /controls + /mappings), so no new backend endpoint is needed.
 *
 * Per-question state:
 *   answered — a control confidence exists (a response was recorded)
 *   review   — no answer yet, but a pending AI mapping is awaiting accept/decline
 *   open     — neither
 */
export type QState = 'answered' | 'review' | 'open'

export type ModuleCoverage = {
  module_id: string
  total: number
  answered: number
  review: number
  open: number
  conf: number // mean control confidence over answered questions (0 if none)
}

export type Coverage = {
  total: number
  answered: number
  review: number
  open: number
  coveragePct: number
  modules: ModuleCoverage[]
  stateByQ: Map<string, QState>
  mappingByQ: Map<string, Mapping> // first pending mapping per question (for inline triage)
}

export function computeCoverage(
  questions: Question[],
  controls: Control[],
  mappings: Mapping[],
  industry: string,
): Coverage {
  const allQ = questions.filter((q) => q.industry === industry)
  const confByQ = new Map(controls.map((c) => [c.question_id, c.control_confidence ?? 0]))
  const mappingByQ = new Map<string, Mapping>()
  for (const m of mappings) {
    if (m.status === 'pending_review' && !mappingByQ.has(m.question_id)) mappingByQ.set(m.question_id, m)
  }

  const stateByQ = new Map<string, QState>()
  const state = (qid: string): QState =>
    confByQ.has(qid) ? 'answered' : mappingByQ.has(qid) ? 'review' : 'open'

  const mods = new Map<string, { total: number; answered: number; review: number; open: number; confSum: number; confN: number }>()
  for (const q of allQ) {
    const s = state(q.question_id)
    stateByQ.set(q.question_id, s)
    const mid = q.module_id ?? '—'
    const m = mods.get(mid) ?? { total: 0, answered: 0, review: 0, open: 0, confSum: 0, confN: 0 }
    m.total++
    m[s]++
    if (confByQ.has(q.question_id)) { m.confSum += confByQ.get(q.question_id)!; m.confN++ }
    mods.set(mid, m)
  }

  const modules: ModuleCoverage[] = [...mods.entries()]
    .map(([module_id, m]) => ({
      module_id, total: m.total, answered: m.answered, review: m.review, open: m.open,
      conf: m.confN ? Math.round(m.confSum / m.confN) : 0,
    }))
    .sort((a, b) => a.module_id.localeCompare(b.module_id))

  const total = allQ.length
  const answered = modules.reduce((a, m) => a + m.answered, 0)
  const review = modules.reduce((a, m) => a + m.review, 0)
  const open = modules.reduce((a, m) => a + m.open, 0)
  return {
    total, answered, review, open,
    coveragePct: total ? Math.round((answered / total) * 100) : 0,
    modules, stateByQ, mappingByQ,
  }
}
